import { api } from './client';
import type { Page } from './catalogs';

/**
 * ч.1296 — оскарження зупиненої реєстрації ПН/РК (СМКОР): повідомлення про подання
 * пояснень та копій документів (J/F1312603), додаткові пояснення (J/F1307801), скарга на
 * рішення про відмову (J/F1313205). Копії документів — вкладення документа, кожне йде
 * окремим файлом J/F1360102 у пакеті; підпис і відправка — Кабінет ДПС.
 */

export type ErpnAppealKind = 'EXPLANATION' | 'ADDITIONAL' | 'COMPLAINT';
export type ErpnAppealResult = 'REGISTERED' | 'REFUSED' | 'MORE_INFO';
export type ErpnSignerKind = 'HEAD' | 'AUTHORIZED';

export const KIND_LABELS: Record<ErpnAppealKind, string> = {
  EXPLANATION: 'Пояснення та копії документів',
  ADDITIONAL: 'Додаткові пояснення',
  COMPLAINT: 'Скарга на рішення про відмову',
};
export const KIND_SHORT: Record<ErpnAppealKind, string> = {
  EXPLANATION: 'пояснення',
  ADDITIONAL: 'додаткові пояснення',
  COMPLAINT: 'скарга',
};
export const RESULT_LABELS: Record<ErpnAppealResult, string> = {
  REGISTERED: 'ПН/РК зареєстровано',
  REFUSED: 'відмовлено',
  MORE_INFO: 'комісія просить додаткові пояснення',
};
/** Ліміт тексту за бланком: повідомлення — 500 символів, скарга — 2000. */
export const TEXT_MAX: Record<ErpnAppealKind, number> = {
  EXPLANATION: 500, ADDITIONAL: 500, COMPLAINT: 2000,
};

export interface ErpnAttachmentMeta {
  name?: string | null;
  number?: string | null;
  date?: string | null;
}

export interface ErpnAppeal {
  id: string;
  organization_id: string;
  invoice_id: string | null;
  kind: ErpnAppealKind;
  document_number: string;
  document_date: string;
  doc_cnt: number;
  explanation_text: string;
  registration_number: number | null;
  decision_number: string | null;
  decision_date: string | null;
  court_appealed: boolean;
  video_participation: boolean;
  video_person: string | null;
  signer_kind: ErpnSignerKind;
  signer_inn: string | null;
  signer_name: string | null;
  attachment_meta: Record<string, ErpnAttachmentMeta>;
  letter: ErpnLetter | Record<string, never>;
  /** ч.1299: ПН/РК введено вручну (invoice_id порожній). */
  manual_invoice: boolean;
  buyer_ipn: string | null;
  amount_base: string | null;
  amount_vat: string | null;
  result: ErpnAppealResult | null;
  result_text: string | null;
  result_doc_number: string | null;
  result_date: string | null;
  comment: string | null;
  deletion_mark: boolean;
  created_at: string;
  submission_status: string | null;
  invoice_number: string | null;
  invoice_date: string | null;
  invoice_is_correction: boolean | null;
  contractor_name: string | null;
  attachments_count: number;
  form_code: string | null;
}

export interface ErpnAppealCreate extends ManualInvoiceFields {
  organization_id: string;
  /** ПН/РК з системи; null — реквізити вручну (invoice_number + invoice_date). */
  invoice_id: string | null;
  kind: ErpnAppealKind;
  document_date?: string | null;
  explanation_text?: string;
  registration_number?: number | null;
  decision_number?: string | null;
  decision_date?: string | null;
  court_appealed?: boolean;
  video_participation?: boolean;
  video_person?: string | null;
  signer_kind?: ErpnSignerKind;
  signer_inn?: string | null;
  signer_name?: string | null;
  comment?: string | null;
}

export interface ErpnAppealPatch extends ManualInvoiceFields {
  document_date?: string | null;
  explanation_text?: string;
  registration_number?: number | null;
  decision_number?: string | null;
  decision_date?: string | null;
  court_appealed?: boolean;
  video_participation?: boolean;
  video_person?: string | null;
  signer_kind?: ErpnSignerKind;
  signer_inn?: string | null;
  signer_name?: string | null;
  attachment_meta?: Record<string, ErpnAttachmentMeta>;
  letter?: ErpnLetter | null;
  comment?: string | null;
}

/** ч.1298: бухгалтерська довідка (пояснення для комісії) — лист на бланку, іде копією J1360102. */
export interface ErpnLetter {
  number?: string | null;
  date?: string | null;
  title?: string | null;
  paragraphs: string[];
  request?: string | null;
  signer_title?: string | null;
  signer_name?: string | null;
  notes?: string[];
  generated_by_ai?: boolean;
  attachment_id?: string | null;
}

/** ч.1299: вкладення іншого документа організації (для копіювання у звернення). */
export interface ServerAttachment {
  id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  created_at: string;
  document_type: string;
  document_id: string;
  relation: 'chain' | 'other';
  doc_number: string | null;
  doc_date: string | null;
  order_kind: string | null;
}

/** ч.1299: реквізити ПН/РК руками — коли зупиненої накладної немає в системі. */
export interface ManualInvoiceFields {
  invoice_number?: string | null;
  invoice_date?: string | null;
  invoice_is_correction?: boolean;
  buyer_name?: string | null;
  buyer_ipn?: string | null;
  amount_base?: string | null;
  amount_vat?: string | null;
}

export interface ErpnAppealCheck {
  errors: { code: string; message: string }[];
  warnings: { code: string; message: string }[];
}

export const erpnAppeals = {
  list: (params?: {
    organization_id?: string; invoice_id?: string; kind?: ErpnAppealKind;
    sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number;
  }) => api.get<Page<ErpnAppeal>>('/erpn-appeals', { params }).then((r) => r.data),
  get: (id: string) => api.get<ErpnAppeal>(`/erpn-appeals/${id}`).then((r) => r.data),
  create: (p: ErpnAppealCreate) => api.post<ErpnAppeal>('/erpn-appeals', p).then((r) => r.data),
  patch: (id: string, p: ErpnAppealPatch) =>
    api.patch<ErpnAppeal>(`/erpn-appeals/${id}`, p).then((r) => r.data),
  remove: (id: string) => api.delete(`/erpn-appeals/${id}`).then(() => undefined),
  check: (id: string) => api.get<ErpnAppealCheck>(`/erpn-appeals/${id}/check`).then((r) => r.data),
  /** ч.1299: вкладення організації на сервері — документи ланцюга ПН/РК окремо. */
  browseAttachments: (params: { organization_id: string; invoice_id?: string; q?: string;
    exclude_appeal_id?: string; limit?: number }) =>
    api.get<{ chain: ServerAttachment[]; items: ServerAttachment[] }>('/erpn-appeals/attachments/browse', { params })
      .then((r) => r.data),
  attachFromExisting: (id: string, attachment_ids: string[]) =>
    api.post<ErpnAppeal>(`/erpn-appeals/${id}/attachments/from-existing`, { attachment_ids }).then((r) => r.data),
  /** ч.1298: довідка — збережена або шаблон з даних (is_template). */
  letter: (id: string) =>
    api.get<{ letter: ErpnLetter; is_template: boolean; title_default: string }>(`/erpn-appeals/${id}/letter`)
      .then((r) => r.data),
  letterTemplate: (id: string) =>
    api.post<ErpnAppeal>(`/erpn-appeals/${id}/letter/template`).then((r) => r.data),
  letterDraft: (id: string) =>
    api.post<{ appeal: ErpnAppeal; notes: string[]; ai: { credits: string; balance_after: string } }>(
      `/erpn-appeals/${id}/letter/draft`).then((r) => r.data),
  letterPdf: (id: string) =>
    api.get(`/erpn-appeals/${id}/letter/pdf`, { responseType: 'blob' }).then((r) => r.data as Blob),
  letterAttach: (id: string) =>
    api.post<ErpnAppeal>(`/erpn-appeals/${id}/letter/attach`).then((r) => r.data),
  /** Файли пакета (основний + копія на кожне вкладення) для підпису у Кабінеті ДПС. */
  files: (id: string) =>
    api.get<{ files: { fname: string; xml_base64: string }[] }>(`/erpn-appeals/${id}/files`)
      .then((r) => r.data),
};
